import React, { useCallback, useEffect, useRef, useState } from 'react';
import './css/CreateProjectUsingAI.css';
import './css/ChatUI.css'; // <-- Add this import for the new CSS
import { callApiWithAuthorization, triggerErrorDialog } from '../../API/CallApi';
import Markdown from 'react-markdown';
import ShapeLoader from './ChatLoader';
import { handleAttributeDiscoveryPrompt, handleEntityDiscoveryPrompt, handleEntityRelationDiscoveryPrompt, handleSchemaDiscoveryPrompt, handleRoleDiscoveryPrompt, handlePortalsDiscoveryPrompt, handleCalculatedFieldPrompt, handlePOAssistantPrompt, handlePageDiscoveryPrompt } from '../../API/AICreateProject/AICreateProject';
import { Dialog } from '@progress/kendo-react-dialogs';
import useUser from '../../Hooks/User/useUser';
import { uuidv4 } from '../../Hooks/Display/utils';
import { postMessageToRuntime } from '../Helpers/Runtime/runtime';

const CreateProjectUsingAI = ({ isInternalUser, organizationId, isAutoMode, autoModeDescription, autoModeFileId }) => {
    const sessionId = useRef(uuidv4())
    const chatPanelRef = useRef(null); // Add ref for chat panel
    const [chatMessages, setChatMessages] = useState([]);
    const [discoveryMessages, setDiscoveryMessages] = useState([]);
    const [entityMessages, setEntityMessages] = useState([]);
    const [attributeMessages, setAttributeMessages] = useState([]); // keep variable name for logic
    const [relationMessages, setRelationMessages] = useState([]);
    const [roleMessages, setRoleMessages] = useState([]);
    const [portalMessages, setPortalMessages] = useState([]);
    const [pageMessages, setPageMessages] = useState([]); // New state for page phase
    const [createAppMessages, setCreateAppMessages] = useState([]);
    const [threadId, setThreadId] = useState(null); // State to store thread ID
    const [threadIds, setThreadIds] = useState({}); // State to store thread IDs for each item
    const [isLoading, setIsLoading] = useState(false); // State to track loading
    const [selectedItem, setSelectedItem] = useState('Overview'); // Changed from 'Discovery'
    const [entities, setEntities] = useState([]); // New state for entities
    const [visited, setVisited] = useState(["Overview"]); // Changed from 'Discovery'
    const [isFirstPromptSent, setIsFirstPromptSent] = useState(false); // State to track if the first prompt is sent
    const [uploadedFile, setUploadedFile] = useState(null); // State to store the uploaded file
    const [totalTokens, setTotalTokens] = useState(0); // State to track total tokens
    const [totalInputTokens, setTotalInputTokens] = useState(0); // State to track total input tokens
    const [isPOBotThinking, setIsPOBotThinking] = useState(false); // State to track if PO Bot is thinking
    const [userLoading, currentUser, userError] = useUser();
    const [isAppCreated, setIsAppCreated] = useState(false); // State to track if the app is created
    const lastHandlerResponseIds = useRef({}); // Use ref instead of state
    const [lastPhase, setLastPhase] = useState('Overview'); // Track the last phase visited

    const phases = ['Overview', 'Object', 'Properties', 'Relations', 'Roles', 'Portals', 'Page', 'Create App']; // Added 'Page'

    // Function to scroll to bottom
    const scrollToBottom = () => {
        if (chatPanelRef.current) {
            chatPanelRef.current.scrollTo({
                top: chatPanelRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    // Auto-scroll when chat messages change
    useEffect(() => {
        scrollToBottom();
    }, [chatMessages]);

    // Auto-scroll when loading states change
    useEffect(() => {
        scrollToBottom();
    }, [isLoading, isPOBotThinking]);

    const moveToNextPhase = () => {
        setSelectedItem((prevSelectedItem) => {
            const currentIndex = phases.indexOf(prevSelectedItem);
            const nextIndex = (currentIndex + 1) % phases.length;
            const nextPhase = phases[nextIndex];

            handleSidebarClick(nextPhase); // Trigger handleSidebarClick for the next phase

            return nextPhase;
        });
    };

    // Function to create app with default configurations
    const createAppWithDefaultConfig = useCallback(async () => {
        if (isAppCreated) {
            return; // Don't create if already created
        }

        try {
            setIsLoading(true);
            let userId = "";
            if (currentUser) {
                if (currentUser.idTokenClaims) {
                    userId = currentUser.idTokenClaims.oid
                } else if (currentUser.homeAccountId) {
                    userId = currentUser.homeAccountId
                } else if (currentUser.azureAccountId) {
                    userId = currentUser.azureAccountId
                } else if (currentUser.userId) {
                    userId = currentUser.userId
                } else if (currentUser.oid) {
                    userId = currentUser.oid
                }
            }

            const response = await callApiWithAuthorization('/api/v0/ai/create-project-from-discovery', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    organizationId,
                    userId: userId,
                    sessionId: sessionId.current
                })
            });
            const resp = await response.json();
            
            if (resp.status === "Success") {
                const appUrl = resp.data.url || resp.data.link || resp.data.Url || resp.data.appUrl;
                setChatMessages(prevMessages => [
                    ...prevMessages,
                    { type: "bot", text: `Project created successfully with default configurations. Use this link to access it: [Click Here](${appUrl}). ${appUrl}` }
                ]);
                setIsAppCreated(true);
                
                // Ensure error dialog is not shown when app is created successfully
                // Error dialog state is managed globally, so we don't need to explicitly hide it
                
                // Handle redirection like other places in the codebase
                if (appUrl) {
                    // Post message to runtime for iframe communication
                    postMessageToRuntime({
                        type: "projectCreated",
                        openAppUrl: appUrl
                    });
                    
                    // Redirect to the app URL
                    window.open(appUrl + "/DefaultPortal", "_self");
                }
            } else {
                // Handle error with id if present
                const errorMessage = resp.message || 'Something went wrong while creating the app.';
                let displayMessage = `Error creating app: ${errorMessage}`;
                
                // If error has an id, show error dialog and include id in UI message
                if (resp.requestId || resp.id) {
                    const errorId = resp.requestId || resp.id;
                    displayMessage = `Error creating app: ${errorMessage}\n\nError ID: ${errorId}`;
                    
                    // Trigger error dialog with error id
                    const error = new Error(errorMessage);
                    error.id = errorId;
                    triggerErrorDialog(error);
                }
                
                setChatMessages((prevMessages) => [
                    ...prevMessages,
                    { type: 'bot', text: displayMessage }
                ]);
            }
        } catch (error) {
            console.error('Error creating app with default config:', error);
            let errorMessage = error.message || 'Failed to create app with default configurations.';
            let displayMessage = `Error creating app: ${errorMessage}`;
            
            // If error has an id, show error dialog and include id in UI message
            if (error.id) {
                displayMessage = `Error creating app: ${errorMessage}\n\nError ID: ${error.id}`;
                triggerErrorDialog(error);
            }
            
            setChatMessages((prevMessages) => [
                ...prevMessages,
                { type: 'bot', text: displayMessage }
            ]);
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, organizationId, isAppCreated, sessionId]);

    useEffect(() => {
        const fetchInitialMessage = async () => {
            setIsLoading(true);
            try {
                const response = await handleSchemaDiscoveryPrompt({ threadId, sessionId: sessionId.current });
                setThreadId(response.threadId);
                setChatMessages((prevMessages) => [
                    ...prevMessages,
                    { type: 'bot', text: response.response, usage: response.usage }
                ]);
                lastHandlerResponseIds.current = { ...lastHandlerResponseIds.current, Overview: response.id }; // Save the response ID for Overview phase
                if (response.usage) {
                    setTotalTokens((prevTotal) => prevTotal + response.usage.total_tokens);
                    setTotalInputTokens((prevTotal) => prevTotal + response.usage.prompt_tokens);
                }
                if (isAutoMode && response.id) {
                    handleAutoModeFlow(autoModeDescription, response.threadId, 'Overview', autoModeFileId);
                }
            } catch (error) {
                console.error('Error fetching initial message:', error);
                // If in auto mode and initial message fails, create app with default config
                if (isAutoMode) {
                    setChatMessages((prevMessages) => [
                        ...prevMessages,
                        { type: 'bot', text: `Oops, something went wrong with the AI, trying to create your app with default configurations.` }
                    ]);
                    await createAppWithDefaultConfig();
                }
            } finally {
                setIsLoading(false);
            }
        };

        if (currentUser) {
            fetchInitialMessage();
        }
    }, [currentUser, isAutoMode, createAppWithDefaultConfig]);

    useEffect(() => {
        if (isAutoMode && threadId) {
            const initialPrompt = autoModeDescription;
            handleAutoModeFlow(initialPrompt, threadId, "Overview", autoModeFileId); // Changed from 'Discovery'
        }
    }, [threadId]);

    const handleAutoModeFlow = async (initialPrompt, currentThreadId, phase="Overview", fileId=null) => { // Changed default phase
        const projectDiscoveryThreadId = threadId;
        let prompt = initialPrompt;
        let previousResponseId = lastHandlerResponseIds.current[phase] || null;

        try {
            while (true) {
                let poResponse;
                try {
                    setIsPOBotThinking(true);
                    // Remove threadId, add previousResponseId
                    const poPreviousResponsId = lastHandlerResponseIds.current["PO Assistant"] || null;
                    poResponse = await handlePOAssistantPrompt({ prompt, previousResponseId: poPreviousResponsId, fileId, sessionId: sessionId.current });
                    fileId = null; // Reset fileId after the first use
                    // Store PO Assistant's last response ID in its own phase
                    lastHandlerResponseIds.current = { ...lastHandlerResponseIds.current, ["PO Assistant"]: poResponse.id };
                    setIsPOBotThinking(false);
                    if (poResponse.usage) {
                        setTotalTokens((prevTotal) => prevTotal + poResponse.usage.total_tokens);
                        setTotalInputTokens((prevTotal) => prevTotal + poResponse.usage.prompt_tokens);
                    }
                    setChatMessages((prevMessages) => [
                        ...prevMessages,
                        { type: 'user', usage: poResponse.usage, text: poResponse.response }
                    ]);
                } catch (poError) {
                    setIsPOBotThinking(false);
                    // Show error message and trigger app creation with default config
                    setChatMessages((prevMessages) => [
                        ...prevMessages,
                        { type: 'bot', text: `Oops, something went wrong with the AI, trying to create your app with default configurations.` }
                    ]);
                    console.error('Error in PO Assistant prompt:', poError);
                    
                    // Create app with default configurations
                    await createAppWithDefaultConfig();
                    break; // Exit the loop on error
                }

                try {
                    // Determine the appropriate handler based on the selected item
                    // Use poResponse.response if available, otherwise fall back to prompt
                    const handlerPrompt = poResponse?.response || prompt;
                    const handlerOptions = { prompt: handlerPrompt, threadId: projectDiscoveryThreadId, previousResponseId, sessionId: sessionId.current };
                    let handlerResponse;

                    setIsLoading(true);
                    switch (phase) {
                        case 'Object':
                            handlerResponse = await handleEntityDiscoveryPrompt(handlerOptions);
                            break;
                        case 'Relations':
                            handlerResponse = await handleEntityRelationDiscoveryPrompt(handlerOptions);
                            break;
                        case 'Properties': // Changed from 'Attributes'
                            handlerResponse = await handleAttributeDiscoveryPrompt(handlerOptions);
                            break;
                        case 'Page':
                            handlerResponse = await handlePageDiscoveryPrompt(handlerOptions);
                            break;
                        case 'Roles':
                            handlerResponse = await handleRoleDiscoveryPrompt(handlerOptions);
                            break;
                        case 'Portals':
                            handlerResponse = await handlePortalsDiscoveryPrompt(handlerOptions);
                            break;
                        case 'Calculated Field':
                            handlerResponse = await handleCalculatedFieldPrompt({
                                ...handlerOptions,
                                projectDiscoveryThreadId: threadId,
                            });
                            break;
                        default:
                            handlerResponse = await handleSchemaDiscoveryPrompt(handlerOptions);
                            break;
                    }

                    setIsLoading(false);
                    // Display the response from the handler
                    const cleanHandlerText = handlerResponse.response ? handlerResponse.response.replace(/\$\$EOD\$\$/g, "") : "";
                    setChatMessages((prevMessages) => [
                        ...prevMessages,
                        { type: 'bot', usage: handlerResponse.usage, text: cleanHandlerText, id: handlerResponse.id }
                    ]);
                    lastHandlerResponseIds.current = { ...lastHandlerResponseIds.current, [phase]: handlerResponse.id };

                    // Update total tokens if usage is present
                    if (handlerResponse.usage) {
                        setTotalTokens((prevTotal) => prevTotal + handlerResponse.usage.total_tokens);
                        setTotalInputTokens((prevTotal) => prevTotal + handlerResponse.usage.prompt_tokens);
                    }

                    // Check for $$EOD$$ token in the handler's response
                    if (handlerResponse.response && handlerResponse.response.includes('$$EOD$$')) {
                        setTimeout(() => {
                            moveToNextPhase();
                        }, 500);
                        break; // Exit the loop
                    }

                    // Update the prompt for the next iteration
                    prompt = handlerResponse.response;
                    previousResponseId = handlerResponse.id;
                } catch (handlerError) {
                    setIsLoading(false);
                    // Debugger for timeout errors - this is where API timeouts (504, CORS, network errors) are caught
                    debugger;
                    // Handle different types of errors
                    let errorMessage = 'An error occurred while processing your request.';
                    if (handlerError.name === 'TypeError' && handlerError.message.includes('Failed to fetch')) {
                        errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
                    } else if (handlerError.message) {
                        errorMessage = handlerError.message;
                    }
                    
                    // Show error message and trigger app creation with default config
                    setChatMessages((prevMessages) => [
                        ...prevMessages,
                        { type: 'bot', text: `Oops, something went wrong with the AI, trying to create your app with default configurations.` }
                    ]);
                    console.error(`Error in ${phase} handler:`, handlerError);
                    
                    // Create app with default configurations
                    await createAppWithDefaultConfig();
                    break; // Exit the loop on error
                }
            }
        } catch (error) {
            debugger;
            // Final catch block for any unexpected errors
            setIsLoading(false);
            setIsPOBotThinking(false);
            // Show error message and trigger app creation with default config
            setChatMessages((prevMessages) => [
                ...prevMessages,
                { type: 'bot', text: `Oops, something went wrong with the AI, trying to create your app with default configurations.` }
            ]);
            console.error('Error in Auto Mode flow:', error);
            
            // Create app with default configurations
            await createAppWithDefaultConfig();
        }
    };

    const addMessageToPhase = (phase, message) => {
        switch (phase) {
            case 'Overview': // Changed from 'Discovery'
                setDiscoveryMessages((prevMessages) => [...prevMessages, message]);
                break;
            case 'Object':
                setEntityMessages((prevMessages) => [...prevMessages, message]);
                break;
            case 'Properties': // Changed from 'Attributes'
                setAttributeMessages((prevMessages) => [...prevMessages, message]);
                break;
            case 'Relations':
                setRelationMessages((prevMessages) => [...prevMessages, message]);
                break;
            case 'Page':
                setPageMessages((prevMessages) => [...prevMessages, message]);
                break;
            case 'Roles':
                setRoleMessages((prevMessages) => [...prevMessages, message]);
                break;
            case 'Portals':
                setPortalMessages((prevMessages) => [...prevMessages, message]);
                break;
            default:
                break;
        }
    };

    const handleSendMessage = async (prompt) => {
        if (prompt.trim() || uploadedFile) {
            setIsFirstPromptSent(true); // Mark the first prompt as sent
            const newMessage = { type: 'user', text: prompt, file: uploadedFile };

            addMessageToPhase(selectedItem, newMessage); // Use the new function
            setChatMessages((prevMessages) => [...prevMessages, newMessage]); // Update chat messages

            setIsLoading(true);
            try {
                const currentThreadId = threadIds[selectedItem] || threadId;
                let fileId = null;

                if (!isAutoMode && uploadedFile) {
                    // Upload the attachment and get the file ID
                    const formData = new FormData();
                    formData.append('file', uploadedFile);

                    try {
                        const response = await callApiWithAuthorization('/api/v0/ai/upload-file', {
                            method: 'POST',
                            body: formData,
                        });

                        const result = await response.json();
                        if (result.id) {
                            fileId = result.id;
                        } else {
                            console.error('File upload failed:', result.message);
                        }
                    } catch (error) {
                        console.error('Error uploading file:', error);
                    } finally {
                        setUploadedFile(null); // Reset uploadedFile state
                    }
                }

                if (isAutoMode) {
                    // Start the Auto Mode flow
                    await handleAutoModeFlow(prompt, currentThreadId);
                } else {
                    // Handle the prompt normally for the selected item
                    let response;
                    const previousResponseId = lastHandlerResponseIds.current[selectedItem] || null;

                    switch (selectedItem) {
                        case 'Object':
                            response = await handleEntityDiscoveryPrompt({ prompt, threadId: currentThreadId, previousResponseId, sessionId: sessionId.current });
                            break;
                        case 'Relations':
                            debugger;
                            response = await handleEntityRelationDiscoveryPrompt({ prompt, threadId: currentThreadId, fileId, previousResponseId, sessionId: sessionId.current });
                            break;
                        case 'Properties': // Changed from 'Attributes'
                            response = await handleAttributeDiscoveryPrompt({ prompt, threadId: currentThreadId, previousResponseId, sessionId: sessionId.current });
                            break;
                        case 'Page':
                            response = await handlePageDiscoveryPrompt({ prompt, threadId: currentThreadId, previousResponseId, sessionId: sessionId.current });
                            break;
                        case 'Roles':
                            response = await handleRoleDiscoveryPrompt({ prompt, threadId: currentThreadId, previousResponseId, sessionId: sessionId.current });
                            break;
                        case 'Portals':
                            response = await handlePortalsDiscoveryPrompt({ prompt, threadId: currentThreadId, previousResponseId, sessionId: sessionId.current });
                            break;
                        case 'Calculated Field':
                            response = await handleCalculatedFieldPrompt({ prompt, threadId: currentThreadId, projectDiscoveryThreadId: threadId, previousResponseId, sessionId: sessionId.current });
                            break;
                        default:
                            response = await handleSchemaDiscoveryPrompt({ prompt, threadId: currentThreadId, fileId, previousResponseId, sessionId: sessionId.current });
                            break;
                    }

                    // Remove all occurrences of $$EOD$$ from the response text
                    const cleanText = response.response ? response.response.replace(/\$\$EOD\$\$/g, "") : "";

                    const botMessage = { type: 'bot', usage: response.usage, text: cleanText, id: response.id };

                    addMessageToPhase(selectedItem, botMessage); // Use the new function
                    setChatMessages((prevMessages) => [...prevMessages, botMessage]); // Update chat messages
                    lastHandlerResponseIds.current = { ...lastHandlerResponseIds.current, [selectedItem]: response.id };

                    // Update total tokens if response.usage is present
                    if (response.usage) {
                        setTotalTokens((prevTotal) => prevTotal + response.usage.total_tokens);
                        setTotalInputTokens((prevTotal) => prevTotal + response.usage.prompt_tokens);
                    }

                    // Check for $$EOD$$ token in the response
                    if (response.response && response.response.includes('$$EOD$$')) {
                        if (response.entities && Array.isArray(response.entities)) {
                            setEntities(response.entities);
                        }
                        setTimeout(() => {
                            moveToNextPhase();
                        }, 500);
                    }
                }
            } catch (error) {
                debugger;
                // Handle different types of errors
                let errorMessage = 'An error occurred while processing your request.';
                if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                    errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                // Show error message to user
                const errorBotMessage = { type: 'bot', text: `Error: ${errorMessage}` };
                addMessageToPhase(selectedItem, errorBotMessage);
                setChatMessages((prevMessages) => [...prevMessages, errorBotMessage]);
                console.error('Error sending prompt:', error);
                
                // If in auto mode, create app with default configurations
                if (isAutoMode) {
                    setChatMessages((prevMessages) => [
                        ...prevMessages,
                        { type: 'bot', text: `Oops, something went wrong with the AI, trying to create your app with default configurations.` }
                    ]);
                    await createAppWithDefaultConfig();
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleSidebarClick = useCallback(async (itemName) => {
        // Prevent refreshing if the same item is clicked again
        if (selectedItem === itemName) {
            return;
        }

        setSelectedItem(itemName); // Update selected item

        // Clear chat messages for the selected phase
        switch (itemName) {
            case 'Overview': // Changed from 'Discovery'
                setChatMessages(discoveryMessages);
                break;
            case 'Object':
                setChatMessages(entityMessages);
                break;
            case 'Properties': // Changed from 'Attributes'
                setChatMessages(attributeMessages);
                break;
            case 'Relations':
                setChatMessages(relationMessages);
                break;
            case 'Page':
                setChatMessages(pageMessages);
                break;
            case 'Roles':
                setChatMessages(roleMessages);
                break;
            case 'Portals':
                setChatMessages(portalMessages);
                break;
            default:
                setChatMessages([]);
                break;
        }

        // Skip calling handlers if the phase has already been visited
        if (visited.includes(itemName) && itemName !== 'Create App') {
            setLastPhase(selectedItem); // Update lastPhase even if skipping handler
            return;
        }

        setIsLoading(true); // Show loading indicator

        try {
            let currentThreadId = threadIds[itemName]; // Check if thread ID exists for the selected item
            let response;
            // Use previous phase's response id for the first call to a new handler
            let previousResponseId;
            if (!visited.includes(itemName)) {
                const currentPhaseIndex = phases.indexOf(itemName);
                const lastPhase = currentPhaseIndex === 0 ? "Overview" : phases[currentPhaseIndex - 1]; // Get the last phase before the current one
                previousResponseId = lastHandlerResponseIds.current[lastPhase] || null; // Use the last phase's response ID
            } else {
                previousResponseId = lastHandlerResponseIds.current[itemName] || null;
            }

            // Define a mapping of item names to their respective handlers
            const handlers = {
                Object: handleEntityDiscoveryPrompt,
                Relations: handleEntityRelationDiscoveryPrompt,
                Properties: handleAttributeDiscoveryPrompt, // Changed from 'Attributes'
                Page: handlePageDiscoveryPrompt,
                Roles: handleRoleDiscoveryPrompt,
                Portals: handlePortalsDiscoveryPrompt,
                'Calculated Field': handleCalculatedFieldPrompt,
                default: handleSchemaDiscoveryPrompt,
            };

            if (itemName === "Create App") {
                // Handle "Create Project" case
                let userId = "";
                if (currentUser) {
                    if (currentUser.idTokenClaims) {
                        userId = currentUser.idTokenClaims.oid
                    } else if (currentUser.homeAccountId) {
                        userId = currentUser.homeAccountId
                    } else if (currentUser.azureAccountId) {
                        userId = currentUser.azureAccountId
                    } else if (currentUser.userId) {
                        userId = currentUser.userId
                    } else if (currentUser.oid) {
                        userId = currentUser.oid
                    }
                }
                console.log(currentUser);
                if (!isAppCreated) {
                    response = await callApiWithAuthorization('/api/v0/ai/create-project-from-discovery', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            organizationId,
                            userId: userId,
                            sessionId: sessionId.current
                        })
                    });
                    const resp = await response.json();
                    if (resp.status === "Success") {
                        const appUrl = resp.data.url || resp.data.link || resp.data.Url || resp.data.appUrl;
                        setChatMessages(prevMessages => [
                            ...prevMessages,
                            { type: "bot", text: `Project created successfully. Use this link to access it: [Click Here](${appUrl}). ${appUrl}` }
                        ]);
                        setIsAppCreated(true); // Set app created state to true
                        
                        // Ensure error dialog is not shown when app is created successfully
                        // Error dialog state is managed globally, so we don't need to explicitly hide it
                        
                        // Handle redirection like other places in the codebase
                        if (appUrl) {
                            // Post message to runtime for iframe communication
                            postMessageToRuntime({
                                type: "projectCreated",
                                openAppUrl: appUrl
                            });
                            
                            // Redirect to the app URL
                            window.open(appUrl + "/DefaultPortal", "_self");
                        }
                    } else {
                        // Handle error with id if present
                        const errorMessage = resp.message || 'Something went wrong.';
                        let displayMessage = `Error: ${errorMessage}`;
                        
                        // If error has an id, show error dialog and include id in UI message
                        if (resp.requestId || resp.id) {
                            const errorId = resp.requestId || resp.id;
                            displayMessage = `Error: ${errorMessage}\n\nError ID: ${errorId}`;
                            
                            // Trigger error dialog with error id
                            const error = new Error(errorMessage);
                            error.id = errorId;
                            triggerErrorDialog(error);
                        }
                        
                        setChatMessages((prevMessages) => [
                            ...prevMessages,
                            { type: 'bot', text: displayMessage }
                        ]);
                        setCreateAppMessages((prevMessages) => [
                            ...prevMessages,
                            { type: 'bot', text: displayMessage }
                        ]);
                    }
                }
            } else {
                const handler = handlers[itemName] || handlers.default;

                // If no thread ID exists, call the handler without a thread ID to get a new one
                if (!currentThreadId) {
                    const options = { threadId: threadId, previousResponseId, sessionId: sessionId.current };
                    if (itemName === "Calculated Field") {
                        options.projectDiscoveryThreadId = threadId; // Pass the project discovery thread ID
                        options.threadId = null; // Set thread ID to null for the initial call
                    }
                    response = await handler(options);
                    currentThreadId = response.threadId; // Extract thread ID from response
                    setThreadIds((prevThreadIds) => ({
                        ...prevThreadIds,
                        [itemName]: currentThreadId, // Store the thread ID for the selected item
                    }));
                } else {
                    const options = { threadId: threadId, previousResponseId, sessionId: sessionId.current };
                    if (itemName === "Calculated Field") {
                        options.projectDiscoveryThreadId = threadId; // Pass the project discovery thread ID
                        options.threadId = null; // Set thread ID to null for the initial call
                    }
                    // If thread ID exists, call the handler with the existing thread ID
                    response = await handler(options);
                }

                const botMessage = { type: 'bot', usage: response.usage, text: response.response, id: response.id };

                addMessageToPhase(itemName, botMessage); // Use the new function
                setChatMessages((prevMessages) => [...prevMessages, botMessage]); // Update chat messages
                lastHandlerResponseIds.current = { ...lastHandlerResponseIds.current, [itemName]: response.id };

                if (response.usage) {
                    setTotalTokens((prevTotal) => prevTotal + response.usage.total_tokens);
                    setTotalInputTokens((prevTotal) => prevTotal + response.usage.prompt_tokens);
                }

                if (isAutoMode) {
                    const initialPrompt = response.response;
                    handleAutoModeFlow(initialPrompt, threadId, itemName);
                }
            }

            // Mark the item as visited
            setVisited((prevVisited) => [...new Set([...prevVisited, itemName])]);
            setLastPhase(selectedItem); // Update lastPhase after handler call
        } catch (error) {
            debugger;
            // Handle different types of errors
            let errorMessage = 'An error occurred while loading this phase.';
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            // Show error message to user
            const errorBotMessage = { type: 'bot', text: `Error: ${errorMessage}` };
            addMessageToPhase(itemName, errorBotMessage);
            setChatMessages((prevMessages) => [...prevMessages, errorBotMessage]);
            console.error('Error handling sidebar click:', error);
            
            // If in auto mode, create app with default configurations
            if (isAutoMode) {
                setChatMessages((prevMessages) => [
                    ...prevMessages,
                    { type: 'bot', text: `Oops, something went wrong with the AI, trying to create your app with default configurations.` }
                ]);
                await createAppWithDefaultConfig();
            }
        } finally {
            setIsLoading(false); // Hide loading indicator
        }
    }, [currentUser, isAutoMode, createAppWithDefaultConfig]);

    async function downloadSpecification() {
        const response = await callApiWithAuthorization('/api/v0/ai/download-project-specification/' + threadId);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'project_specification.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url); // Clean up the URL object
    }

    return (
        <div className="ai-create-app-root">
            {/* Sidebar */}
            <div className="ai-sidebar">
                <h2 className="ai-sidebar-title">
                    App Model
                </h2>
                <ul className="ai-sidebar-list">
                    {['Overview', 'Object', 'Properties', 'Relations', 'Roles', 'Portals', 'Page'].map((item) => (
                        <li key={item} className="ai-sidebar-item">
                            <div
                                className={`ai-sidebar-link ${selectedItem === item ? 'active' : ''} d-flex align-items-center`}
                                onClick={() => visited.includes(item) && !isAutoMode && handleSidebarClick(item)}
                                style={{
                                    pointerEvents: visited.includes(item) && !isAutoMode ? 'auto' : 'none',
                                    opacity: visited.includes(item) ? 1 : 0.5,
                                }}
                            >
                                {
                                    selectedItem === item && (
                                        <input
                                            type="checkbox"
                                            className="ai-sidebar-checkbox"
                                            checked={selectedItem === item}
                                            readOnly
                                            value={selectedItem === item ? 'checked' : 'unchecked'}
                                        />
                                    )
                                }
                                <div className='ai-overview-item'>
                                    {item}
                                </div>
                            </div>
                            {item === 'Properties' && selectedItem === 'Properties' && (
                                <ul className="ai-sidebar-sublist">
                                    {entities.map((child, index) => (
                                        <li
                                            key={index}
                                            className="ai-sidebar-subitem"
                                            onClick={() => setSelectedItem(child)}
                                            style={{
                                                color: selectedItem === child ? '#007bff' : '#6c757d',
                                                fontWeight: selectedItem === child ? 'bold' : 'normal',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {child}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            {/* Main Content Area */}
            <div className="ai-main-content">
                {/* Chat Panel */}
                <div className="ai-chat-panel" ref={chatPanelRef}>
                    {chatMessages.map((message, index) => (
                        <div
                            key={index}
                            className={`ai-chat-row ${message.type === 'bot' ? 'ai-chat-row-bot' : 'ai-chat-row-user'}`}
                        >
                            <div className={`ai-chat-bubble ${message.type === 'bot' ? 'ai-chat-bubble-bot' : 'ai-chat-bubble-user'}`}>
                                {message.type === 'bot' && (
                                    <div className='d-flex align-items-center'>
                                        <div className="ai-avatar ai-avatar-bot">
                                            <i className='fa-solid fa-robot'/>
                                        </div>
                                        <div className='ai-sender-name'>
                                            &nbsp;Dev Agent
                                        </div>
                                    </div>
                                )}
                                {message.type === 'user' && (
                                    <div className='d-flex align-items-center'>
                                        <div className="ai-avatar ai-avatar-user">
                                            <i className='fa-solid fa-user' />
                                        </div>
                                        <div className='ai-sender-name'>
                                            &nbsp;
                                            {
                                                isAutoMode ? "BA Agent" : "You"
                                            }
                                        </div>
                                    </div>
                                )}
                                <Markdown
                                    components={{
                                        a: (props) => (
                                            <a href={props.href} target="_blank" rel="noreferrer">
                                                {props.children}
                                            </a>
                                        ),
                                    }}
                                    className="markdown-content"
                                >
                                    {message.text}
                                </Markdown>
                                {/* Improved attached file display */}
                                {message.file && (
                                    <div className="ai-chat-file-attachment">
                                        <i className="fa-solid fa-paperclip ai-chat-file-icon" style={{marginRight: '6px'}}></i>
                                        <span className="ai-chat-file-label">Attached File:</span>
                                        <a
                                            href={URL.createObjectURL(message.file)}
                                            download={message.file.name}
                                            className="ai-chat-file-link"
                                        >
                                            {message.file.name} <span className="ai-chat-file-size">({(message.file.size / 1024).toFixed(2)} KB)</span>
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="ai-chat-row ai-chat-row-bot">
                            <div className="ai-avatar ai-avatar-bot">
                                <i className='fa-solid fa-robot'/>
                            </div>
                            <div className="ai-chat-bubble ai-chat-bubble-bot">
                                <ShapeLoader />
                            </div>
                        </div>
                    )}
                    {isPOBotThinking && (
                        <div className="ai-chat-row ai-chat-row-user">
                            <div className="ai-avatar ai-avatar-user">
                                <i className='fa-solid fa-user' />
                            </div>
                            <div className="ai-chat-bubble ai-chat-bubble-user">
                                <ShapeLoader />
                            </div>
                        </div>
                    )}
                </div>

                {/* Input Area */}
                {!isAutoMode && (
                    <div className="ai-chat-input-row">
                        {selectedItem === "Overview" && !isFirstPromptSent && (
                            <label className="ai-upload-btn">
                                <input
                                    type="file"
                                    accept=".docx,.pdf,.xlsx"
                                    style={{ display: 'none' }}
                                    disabled={selectedItem !== visited[visited.length - 1]}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) setUploadedFile(file);
                                    }}
                                />
                                <i className="fa-solid fa-file-upload"></i>
                            </label>
                        )}
                        <input
                            className="ai-chat-input"
                            placeholder="Type your prompt..."
                            disabled={selectedItem !== visited[visited.length - 1]}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSendMessage(e.target.value);
                                    e.target.value = '';
                                }
                            }}
                        />
                        <button
                            className="ai-send-btn"
                            disabled={selectedItem !== visited[visited.length - 1]}
                            onClick={() => {
                                const input = document.querySelector('.ai-chat-input');
                                handleSendMessage(input.value);
                                input.value = '';
                            }}
                        >
                            Send
                        </button>
                    </div>
                )}
                {uploadedFile && !isAutoMode && (
                    <div className="ai-file-info">
                        Attached File: <span>{uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(2)} KB)</span>
                    </div>
                )}
                {isInternalUser &&
                    <div className="ai-token-info">
                        Model: GPT-4.1-mini | Total Tokens: {totalTokens} | Input Tokens: {totalInputTokens}
                    </div>
                }
            </div>
        </div>
    );
};

export default CreateProjectUsingAI;